import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { blogService } from '../../services';
import { buildCategoryTree } from '../../utils/blog';
import { LoadingSpinner } from '../../components/ui/Loading';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import PageContainer from '../../components/PageContainer';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const AdminBlogCategories = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, categoryId: null });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await blogService.getCategories();
      setCategories(res.categories || []);
    } catch (e) {
      console.error('Erro ao carregar categorias:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'ADMIN') load();
  }, [user, load]);

  const handleCreate = async (ev) => {
    ev.preventDefault();
    if (!name.trim()) { notify.error('Nome é obrigatório', 'Erro'); return; }
    try {
      setSaving(true);
      await blogService.createCategory({ name: name.trim(), parentId: parentId || null });
      notify.success('Categoria criada!', '✅ Blog');
      setName('');
      setParentId('');
      await load();
    } catch (e) {
      notify.error(e.response?.data?.error?.parentId || 'Erro ao criar categoria', 'Erro');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const categoryId = confirmModal.categoryId;
    setConfirmModal({ isOpen: false, categoryId: null });
    try {
      await blogService.deleteCategory(categoryId);
      notify.success('Categoria excluída!', '🗑️ Blog');
      await load();
    } catch (e) {
      // 409 => has children or posts
      notify.error(e.response?.data?.error || 'Erro ao excluir categoria', 'Não foi possível excluir');
    }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <PageContainer title="Categorias do Blog">
        <p className="text-red-400">Acesso negado. Apenas administradores.</p>
      </PageContainer>
    );
  }

  const tree = buildCategoryTree(categories);
  const parents = categories.filter((c) => !c.parentId);

  return (
    <PageContainer title="Categorias do Blog" description="Organize os posts em categorias (até dois níveis)">
      <Card variant="glass" className="mb-8">
        <div className="p-6">
          <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome da categoria"
              className="flex-1 px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white"
            />
            <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white">
              <option value="">Sem categoria mãe</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <Button type="submit" disabled={saving} loading={saving}>
              Adicionar
            </Button>
          </form>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : tree.length === 0 ? (
        <Card variant="glass">
          <div className="p-6 text-center text-gray-400">Nenhuma categoria cadastrada.</div>
        </Card>
      ) : (
        <Card variant="glass">
          <div className="divide-y divide-white/10">
            {tree.map((parent) => (
              <div key={parent.id} className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{parent.name}</span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setConfirmModal({ isOpen: true, categoryId: parent.id })}
                  >
                    Excluir
                  </Button>
                </div>
                {parent.children.length > 0 && (
                  <div className="mt-2 pl-4 space-y-1">
                    {parent.children.map((child) => (
                      <div key={child.id} className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">↳ {child.name}</span>
                        <Button
                          variant="danger"
                          size="xs"
                          onClick={() => setConfirmModal({ isOpen: true, categoryId: child.id })}
                        >
                          Excluir
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, categoryId: null })}
        onConfirm={confirmDelete}
        title="Excluir Categoria"
        message="Tem certeza? Categorias com subcategorias ou posts não podem ser excluídas."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </PageContainer>
  );
};

export default AdminBlogCategories;
